import React, { useState, useEffect, useCallback, useRef } from "react";
import api, { API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { fmtMoney, fmtNum, fmtPct, today } from "@/lib/format";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Upload, Download, FileText, Loader2, Trash2, CalendarPlus, Plus,
} from "lucide-react";
import StatTable from "@/components/StatTable";

const EVENT_TYPES = ["campaign", "creative-test", "review", "launch"];
const typeColor = (t) => ({
  campaign: "text-lime-400 border-lime-400/30 bg-lime-400/10",
  "creative-test": "text-cyan-400 border-cyan-400/30 bg-cyan-400/10",
  review: "text-amber-400 border-amber-500/30 bg-amber-500/10",
  launch: "text-rose-400 border-rose-500/30 bg-rose-500/10",
}[t] || "text-zinc-400");

export default function Reports() {
  const { user } = useAuth();
  const cur = user?.currency || "USD";
  const fileRef = useRef();
  const [weekly, setWeekly] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [evForm, setEvForm] = useState({ title: "", date: today(), type: "campaign", notes: "" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [w, c] = await Promise.all([api.get("/reports/weekly"), api.get("/calendar")]);
      setWeekly(w.data.weekly);
      setEvents(c.data.events);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const res = await api.post("/import/csv", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Imported ${res.data.imported} rows (labeled "imported")`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Import failed");
    } finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const downloadTemplate = async () => {
    const res = await api.get("/import/template");
    const blob = new Blob([res.data.sample], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "karyow_import_template.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  const exportCsv = () => { window.open(`${API}/reports/export`, "_blank"); };

  const clearImported = async () => {
    const res = await api.delete("/data/imported");
    toast.success(`Removed ${res.data.deleted} imported rows`);
    load();
  };

  const addEvent = async () => {
    if (!evForm.title.trim()) { toast.error("Event title required"); return; }
    await api.post("/calendar", evForm);
    toast.success("Added to calendar");
    setEvForm({ title: "", date: today(), type: "campaign", notes: "" });
    load();
  };

  const delEvent = async (id) => { await api.delete(`/calendar/${id}`); load(); };

  return (
    <div className="space-y-6 animate-fade-up">
      {/* Import / Export toolbar */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-[#14181B] border border-[#242B30] rounded-xl p-5">
          <div className="flex items-center gap-2 mb-3">
            <Upload className="h-4 w-4 text-lime-400" />
            <h3 className="font-display font-semibold text-white">CSV Import Workflow</h3>
          </div>
          <p className="text-sm text-zinc-400 mb-4">Import Meta Ads Manager exports. Rows are labeled <span className="font-mono text-cyan-400">imported</span> and blended into your dashboards.</p>
          <input ref={fileRef} type="file" accept=".csv" onChange={upload} className="hidden" data-testid="csv-file-input" />
          <div className="flex flex-col sm:flex-row sm:flex-wrap gap-2">
            <button data-testid="import-csv-btn" onClick={() => fileRef.current?.click()} disabled={uploading}
              className="inline-flex items-center justify-center gap-2 rounded-full bg-lime-400 hover:bg-lime-300 px-4 min-h-[44px] text-sm font-semibold text-[#0A0D0E] disabled:opacity-60">
              {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Upload CSV
            </button>
            <button data-testid="template-btn" onClick={downloadTemplate} className="inline-flex items-center justify-center gap-2 rounded-full border border-[#242B30] text-zinc-300 hover:text-white px-4 min-h-[44px] text-sm">
              <FileText className="h-4 w-4" /> Template
            </button>
            <button data-testid="clear-imported-btn" onClick={clearImported} className="inline-flex items-center justify-center gap-2 rounded-full border border-[#242B30] text-zinc-400 hover:text-rose-400 px-4 min-h-[44px] text-sm">
              <Trash2 className="h-4 w-4" /> Clear Imported
            </button>
          </div>
        </div>

        <div className="bg-[#14181B] border border-[#242B30] rounded-xl p-5">
          <div className="flex items-center gap-2 mb-3">
            <Download className="h-4 w-4 text-lime-400" />
            <h3 className="font-display font-semibold text-white">Export & Data</h3>
          </div>
          <p className="text-sm text-zinc-400 mb-4">Download a CSV of every performance row currently in your workspace (live Meta + imported).</p>
          <div className="flex flex-col sm:flex-row sm:flex-wrap gap-2">
            <button data-testid="export-csv-btn" onClick={exportCsv} className="inline-flex items-center justify-center gap-2 rounded-full bg-lime-400 hover:bg-lime-300 px-4 min-h-[44px] text-sm font-semibold text-[#0A0D0E]">
              <Download className="h-4 w-4" /> Export CSV
            </button>
          </div>
        </div>
      </div>

      {/* Weekly summaries */}
      <div className="bg-[#14181B] border border-[#242B30] rounded-xl overflow-hidden">
        <div className="p-5 border-b border-[#242B30]"><h3 className="font-display font-semibold text-white">Weekly Performance Summary</h3></div>
        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="h-5 w-5 text-lime-400 animate-spin" /></div>
        ) : weekly.length === 0 ? (
          <div className="py-16 text-center text-zinc-400">No data.</div>
        ) : (
          <StatTable
            testIdPrefix="weekly"
            rowKey={(w) => w.week}
            rows={weekly}
            columns={[
              { key: "week", label: "Week", primary: true },
              { key: "spend", label: "Spend", render: (w) => fmtMoney(w.spend, cur) },
              { key: "reach", label: "Reach", render: (w) => fmtNum(w.reach) },
              { key: "impressions", label: "Impr.", render: (w) => fmtNum(w.impressions) },
              { key: "cpm", label: "CPM", render: (w) => fmtMoney(w.cpm, cur) },
              { key: "ctr", label: "CTR", render: (w) => fmtPct(w.ctr) },
              { key: "cpc", label: "CPC", render: (w) => fmtMoney(w.cpc, cur) },
              { key: "landing_page_views", label: "LP Views", render: (w) => fmtNum(w.landing_page_views) },
              { key: "conversions", label: "Conv.", render: (w) => fmtNum(w.conversions) },
            ]}
          />
        )}
      </div>

      {/* Calendar */}
      <div className="bg-[#14181B] border border-[#242B30] rounded-xl p-5">
        <div className="flex items-center gap-2 mb-4">
          <CalendarPlus className="h-4 w-4 text-lime-400" />
          <h3 className="font-display font-semibold text-white">Marketing Calendar & Creative Tests</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-12 gap-3 mb-5">
          <Input data-testid="event-title" placeholder="Event / test title" value={evForm.title}
            onChange={(e) => setEvForm({ ...evForm, title: e.target.value })}
            className="md:col-span-4 bg-[#0F1315] border-[#242B30] text-sm" />
          <Input data-testid="event-date" type="date" value={evForm.date}
            onChange={(e) => setEvForm({ ...evForm, date: e.target.value })}
            className="md:col-span-3 bg-[#0F1315] border-[#242B30] font-mono text-sm [color-scheme:dark]" />
          <div className="md:col-span-3">
            <Select value={evForm.type} onValueChange={(v) => setEvForm({ ...evForm, type: v })}>
              <SelectTrigger data-testid="event-type" className="bg-[#0F1315] border-[#242B30]"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#14181B] border-[#242B30] text-white">
                {EVENT_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <button data-testid="add-event-btn" onClick={addEvent}
            className="md:col-span-2 inline-flex items-center justify-center gap-2 rounded-lg bg-lime-400 hover:bg-lime-300 px-4 min-h-[44px] text-sm font-semibold text-[#0A0D0E]">
            <Plus className="h-4 w-4" /> Add
          </button>
        </div>

        {events.length === 0 ? (
          <p className="text-sm text-zinc-500 font-mono">No scheduled events yet.</p>
        ) : (
          <div className="space-y-2">
            {[...events].sort((a, b) => a.date.localeCompare(b.date)).map((ev) => (
              <div key={ev.id} data-testid={`event-${ev.id}`} className="flex items-center gap-2.5 sm:gap-3 bg-[#0F1315] border border-[#242B30] rounded-lg px-3 py-2.5 flex-wrap">
                <span className="font-mono text-xs text-zinc-400 shrink-0">{ev.date}</span>
                <span className={`text-[10px] font-mono uppercase px-1.5 py-0.5 rounded border shrink-0 ${typeColor(ev.type)}`}>{ev.type}</span>
                <span className="text-sm text-zinc-200 flex-1 min-w-0 truncate">{ev.title}</span>
                <button onClick={() => delEvent(ev.id)} data-testid={`del-event-${ev.id}`}
                  className="p-2 -mr-2 text-zinc-600 hover:text-rose-400 shrink-0"><Trash2 className="h-4 w-4" /></button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
